import { Component, Element, h, Host, Listen, Prop, setMode, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { ContentAggregatorItemLayoutAlignment } from "../content-aggregator/constants/content-aggregator-types";
import { CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_LEFT } from "../content-aggregator/constants/content-aggregator-constants";
import {
  ContentAggregatorItemHeadlineSize,
  ContentAggregatorItemLayout,
  ContentAggregatorItemSchemeDefinition,
  ContentAggregatorItemSchemeName
} from "./constants/content-aggregator-item-types";
import {
  CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED,
  CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE,
  CONTENT_AGGREGATOR_ITEM_SCHEME_CONFIG,
  CONTENT_AGGREGATOR_ITEM_SCHEME_MAP
} from "./constants/content-aggregator-item-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { HeadlineSize, HeadlineTag } from "../headline/constants/headline-types";
import { DateTypes } from "../date-event/constants/date-event-types";
import { DATE_TYPE_ONE, DATE_TYPE_THREE, DATE_TYPE_TWO } from "../date-event/constants/date-event-constants";
import { getFirstNestedChild } from "../../utils/dom-helper";
import { isDesktopMedium, isDesktopOrLarger, isMobile, isTablet, isTabletOrLarger } from "../../utils/screen-helper";
import { shortenString, stripHtml } from "../../utils/string-format";
import { ButtonSchemeDefinition, ButtonTarget } from "../button/constants/button-types";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import {
  CONTENT_AGGREGATOR_ITEM_ALTERNATIVE_TAG_PILL_SCHEME,
  CONTENT_AGGREGATOR_ITEM_HEADLINE_LIST_LARGE,
  CONTENT_AGGREGATOR_ITEM_HEADLINE_LIST_SMALL,
  CONTENT_AGGREGATOR_ITEM_HEADLINE_TILE_LARGE,
  CONTENT_AGGREGATOR_ITEM_HEADLINE_TILE_SMALL
} from "../../constants/global-constants";
import { ButtonSchemeConfiguration, ContentAggregatorItemSchemeConfiguration } from "../../constants/colors";
import { createTagPillMapDefinition } from "../../utils/scheme-helper";
import { BackgroundColor } from "../background/constants/background-types";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot image - You can put an <eon-ui-image> component in that slot.
 * @slot tag-pill-group - You can put an <eon-ui-tag-pill-group> component in that slot.
 * @slot content - You can put any (reasonable) number of content in that slot viz: `<p>, <div> <span>`.
 * @slot links - You should put only <eon-ui-link> component in that slot. Since the link behaviour on click of headline depends on "href" given to the link component.
 */

@Component({
  tag: "eon-ui-content-aggregator-item",
  styleUrls: {
    tile: "content-aggregator-item-tile.scss",
    list: "content-aggregator-item-list.scss",
    list_expanded: "content-aggregator-item-list_expanded.scss"
  },
  shadow: true
})
export class ContentAggregatorItem
  implements ColorableComponent<ContentAggregatorItemSchemeConfiguration<ContentAggregatorItemSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ContentAggregatorItemSchemeConfiguration<ContentAggregatorItemSchemeDefinition> = CONTENT_AGGREGATOR_ITEM_SCHEME_CONFIG;
  schemeMap = CONTENT_AGGREGATOR_ITEM_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ContentAggregatorItemSchemeDefinition;
  @State() validDate: Date;
  @State() noContentAvailable: boolean;
  @State() timeFormat: string;
  @State() teaserDate: number;
  @State() teaserMonth: string;
  @State() teaserYear: number;
  @State() backgroundImage: boolean = false;
  @State() itemHeadlineSize: HeadlineSize;
  @State() itemHeadlineHtmlTag: string;
  @State() headlineHref: string;
  @State() linkTarget: string;
  @State() ctaLink: HTMLEonUiLinkElement;
  @State() dateElement: HTMLEonUiDateEventElement;
  @State() textContent: string;
  @State() truncatedHeadline: string;
  @State() truncatedBody: string;
  @State() aggregatorDate: number;
  @State() aggregatorMonth: string;
  @State() aggregatorYear: number;
  @State() isMobile: boolean;
  @State() isTablet: boolean;
  @State() isTabletOrLarger: boolean;
  @State() isDesktopMedium: boolean;
  @State() hasTagPillGroup: HTMLEonUiImageElement;
  @State() hasImage: boolean;
  @State() isDesktopOrLarger: boolean;
  @State() dateEventType: DateTypes;

  @Element() hostElement: HTMLEonUiContentAggregatorItemElement;
  private anchorElement: HTMLAnchorElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ContentAggregatorItemSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The text of the eon-ui-headline inside the teaser (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * Determines the layout of the list item's content when variant is set to `list_expanded`
   * @example default: left
   */
  @Prop() alignLayout: ContentAggregatorItemLayoutAlignment = CONTENT_AGGREGATOR_ITEM_LAYOUT_ALIGNMENT_LEFT;

  /**
   * Determines whether the list expanded items will be showed with alternating background.
   */
  @Prop() zebra: boolean;

  /**
   * Replaces background colour with darker alternative colour shade
   */
  @Prop() shade: boolean;

  /**
   * The size of the headline inside the teaser
   * @example: large | small
   */
  @Prop({ reflect: true }) headlineSize: ContentAggregatorItemHeadlineSize = CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE;

  /**
   * This property can have category as string, accordingly the value will be displayed on Teaser
   */
  @Prop({ mutable: true }) category: string;

  /**
   * This property hides or shows the date property
   */
  @Prop({ reflect: true, mutable: true }) dateInput: boolean = true;

  /**
   * Property will hide and show the content/abstract
   */
  @Prop({ reflect: true, mutable: true }) showAbstract: boolean = true;

  /**
   * Sets a layout for a Aggregator variant component
   * @disableControlInStorybook true
   * @example tile | list | list_expanded
   */
  @Prop({ reflect: true, mutable: true }) variant: ContentAggregatorItemLayout = CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE;

  /**
   * This property can have a date, accordingly the value will be displayed on the Teaser
   */
  @Prop({ mutable: true }) date: string;

  /**
   * This property will hide and show the time format in date section
   */
  @Prop({ reflect: true, mutable: true }) hideTime: boolean = false;

  /**
   * timeZone property for uniform standard time, gives flexibility to authors to edit the timeZone
   * @example: CET/CEST
   */
  @Prop({ mutable: true }) timeZone: string;

  /**
   * twelveHourFormat depicts whether date should be of 12 hours format or 24 hours format
   */
  @Prop({ reflect: true, mutable: true }) twelveHourFormat: boolean = false;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * headlineCharacterLimit will limit the charaters of headline to the given value
   * @disableControlInStorybook true
   * @example default: 50
   */
  @Prop({ reflect: true }) headlineCharacterLimit: number = 50;

  /**
   * copyCharacterLimit will limit the charaters of copy text to the given value
   * @disableControlInStorybook true
   * @example default: 125
   */
  @Prop({ reflect: true }) copyCharacterLimit: number = 125;

  componentWillLoad() {
    this.applyScheme();
    this.imageAsBackground();
    this.convertHeadlineIntoLink();
    this.onWindowresize();
    this.handleHeadlineTag();
    this.hasTagPillGroup = getFirstNestedChild(this.hostElement, "eon-ui-tag-pill-group");
    this.hasImage = getFirstNestedChild(this.hostElement, "eon-ui-image");
  }

  componentDidLoad() {
    this.setCssVariables();
    this.convertHeadlineIntoDownloadLink();
  }

  @Watch("variant")
  @Watch("headlineSize")
  imageAsBackground() {
    const image: HTMLEonUiImageElement = getFirstNestedChild(this.hostElement, "eon-ui-image");
    if (image) {
      image.setAttribute("aspect-ratio", "ratio-2x1");
      this.backgroundImage = true;
    } else {
      this.backgroundImage = false;
    }

    // Given that variant is not a set to `list` the headline type will set to `h3`
    // when the headline size is set to `large` and `h4` for `small`.
    // If it is set to list the item headline size will be set to `h5` for `large
    // and `copy` for `small`.
    if (this.variant !== CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST) {
      this.itemHeadlineSize =
        this.headlineSize === CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE
          ? CONTENT_AGGREGATOR_ITEM_HEADLINE_TILE_LARGE
          : CONTENT_AGGREGATOR_ITEM_HEADLINE_TILE_SMALL;
    } else {
      this.itemHeadlineSize =
        this.headlineSize === CONTENT_AGGREGATOR_ITEM_LARGE_HEADLINE
          ? CONTENT_AGGREGATOR_ITEM_HEADLINE_LIST_LARGE
          : CONTENT_AGGREGATOR_ITEM_HEADLINE_LIST_SMALL;
    }
  }

  convertHeadlineIntoLink() {
    this.ctaLink = getFirstNestedChild(this.hostElement, "eon-ui-link");
    // Below the anchor tag wrapped around headline gets all the properties that slotted eon-ui-link has.
    if (this.ctaLink) {
      this.headlineHref = this.ctaLink.getAttribute("href");
      this.linkTarget = this.ctaLink.getAttribute("target");
    }
  }

  convertHeadlineIntoDownloadLink() {
    if (this.ctaLink && this.anchorElement && this.ctaLink.hasAttribute("download")) {
      this.anchorElement.setAttribute("download", this.ctaLink.getAttribute("download"));
    }
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.contentBackground;

    if (this.zebra && this.hostElement.querySelectorAll("EON-UI-TAG-PILL")) {
      const tagPillSchemeMapDefinition = createTagPillMapDefinition();
      const tagPills = Array.from(this.hostElement.querySelectorAll("EON-UI-TAG-PILL"));
      tagPills.forEach((tagPill) => {
        tagPill.setAttribute(
          "scheme",
          this.activeScheme.backgroundColor
            ? tagPillSchemeMapDefinition[this.activeScheme.backgroundColor as BackgroundColor]
            : CONTENT_AGGREGATOR_ITEM_ALTERNATIVE_TAG_PILL_SCHEME
        );
      });
    }
  }

  @Watch("variant")
  setMode() {
    setMode(() => {
      return this.variant;
    });
  }

  @Listen("resize", { target: "window" })
  onWindowresize() {
    this.isMobile = isMobile();
    this.isTablet = isTablet();
    this.isTabletOrLarger = isTabletOrLarger();
    this.isDesktopMedium = isDesktopMedium();
    this.isDesktopOrLarger = isDesktopOrLarger();

    // When the variant is set to `list_expanded` the date type is `three` on mobile, `one` on tablet or larger
    // when an image is present but otherwise `two`.
    if (this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED) {
      this.dateEventType =
        this.isTabletOrLarger && this.hasImage ? DATE_TYPE_ONE : this.isMobile ? DATE_TYPE_THREE : DATE_TYPE_TWO;
    } else {
      this.dateEventType =
        this.isDesktopOrLarger || this.isTablet ? DATE_TYPE_TWO : this.isMobile ? DATE_TYPE_THREE : DATE_TYPE_TWO;
    }

    this.getTruncatedHeadline();
    this.getTruncatedBody();
  }

  @Watch("headlineCharacterLimit")
  @Watch("headlineText")
  private getTruncatedHeadline(): void {
    if (
      this.headlineText &&
      this.headlineText.length > this.headlineCharacterLimit &&
      this.variant !== CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED
    ) {
      this.truncatedHeadline = shortenString(this.headlineText, this.headlineCharacterLimit, "...");
    } else {
      this.truncatedHeadline = this.headlineText;
    }
  }

  @Watch("copyCharacterLimit")
  private getTruncatedBody() {
    Array.from(this.hostElement.querySelectorAll('[slot="content"]')).map((item) => {
      this.textContent = item.innerHTML;
    });
    const bodyLength = this.textContent && stripHtml(this.textContent).length;
    if (bodyLength > this.copyCharacterLimit) {
      this.truncatedBody = shortenString(this.textContent, this.copyCharacterLimit, "...");
    } else {
      this.truncatedBody = this.textContent;
    }
    Array.from(this.hostElement.querySelectorAll('[slot="content"]')).map((item) => {
      item.innerHTML = this.truncatedBody;
    });
  }

  handleDateEventComponent() {
    return (
      <eon-ui-date-event
        date={this.date}
        time-zone={this.timeZone}
        twelve-hour-format={this.twelveHourFormat}
        hide-time={this.hideTime}
        date-event-type={this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE ? DATE_TYPE_ONE : this.dateEventType}
      ></eon-ui-date-event>
    );
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.itemHeadlineHtmlTag = useTheRightHtmlTagForHeadline(
      this.headlineHtmlTag,
      this.itemHeadlineSize === "copy" ? "span" : this.itemHeadlineSize
    );
  }

  handleHeadlineComponent() {
    return (
      <eon-ui-headline
        text={this.truncatedHeadline}
        size={this.itemHeadlineSize}
        headline-html-tag={this.itemHeadlineHtmlTag}
      ></eon-ui-headline>
    );
  }

  render() {
    return (
      <Host>
        {this.variant !== CONTENT_AGGREGATOR_ITEM_LAYOUT_TILE ? (
          <a
            href={this.headlineHref}
            class={pfx({
              [`content-aggregator-item__${this.variant}`]: true,
              ["content-aggregator-item__list_expanded--zebra"]: this.zebra,
              ["content-aggregator-item__list_expanded--shade"]: this.shade
            })}
            tabindex="0"
            target={this.linkTarget}
          >
            <div
              class={pfx({
                [`content-aggregator-item__${this.variant}--wrapper`]: true,
                [`${this.scheme}`]: true,
                ["content-aggregator-item__list_expanded--no-abstract"]: !this.showAbstract,
                ["content-aggregator-item__list_expanded--no-image"]: !this.hasImage,
                [`content-aggregator-item__list_expanded--layout-align-${this.alignLayout}`]: true
              })}
            >
              {this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED && (
                <div class={pfx("content-aggregator-item__list_expanded--wrapper-section")}>
                  {this.hasImage && (
                    <div class={pfx("content-aggregator-item__list_expanded--image")}>
                      <slot name="image"></slot>
                    </div>
                  )}

                  {this.hasTagPillGroup && (
                    <div class={pfx("content-aggregator-item__list_expanded--tag-pill-group")}>
                      <slot name="tag-pill-group"></slot>
                    </div>
                  )}
                </div>
              )}

              {this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST && (
                <div
                  class={pfx({
                    [`content-aggregator-item__${this.variant}--date`]: true,
                    ["hour-format"]: !this.twelveHourFormat && this.dateInput,
                    [`${this.hideTime ? "hide-time" : "show-time"}`]: this.dateInput,
                    ["list-category"]: this.category && !this.dateInput,
                    [`size-${this.itemHeadlineSize}`]: this.itemHeadlineSize
                  })}
                >
                  {this.dateInput && this.date ? (
                    this.handleDateEventComponent()
                  ) : (
                    <div class={pfx(`content-aggregator-item__${this.variant}--category`)}>{this.category}</div>
                  )}
                </div>
              )}

              <div class={pfx(`content-aggregator-item__${this.variant}--content size-${this.itemHeadlineSize}`)}>
                {this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED && (
                  <div
                    class={pfx({
                      [`content-aggregator-item__${this.variant}--date`]: true,
                      ["hour-format"]: !this.twelveHourFormat && this.dateInput,
                      [`${this.hideTime ? "hide-time" : "show-time"}`]: this.dateInput,
                      ["list-category"]: this.category && !this.dateInput
                    })}
                  >
                    {this.dateInput && this.date ? (
                      this.handleDateEventComponent()
                    ) : (
                      <div class={pfx(`content-aggregator-item__${this.variant}--category`)}>{this.category}</div>
                    )}
                  </div>
                )}

                <div class={pfx({ [`content-aggregator-item__${this.variant}--headline`]: true })}>
                  {this.handleHeadlineComponent()}
                </div>
                {this.showAbstract && (
                  <div class={pfx(`content-aggregator-item__${this.variant}--body`)}>
                    <eon-ui-text text-style="copy" font-weight="400">
                      {this.truncatedBody}
                    </eon-ui-text>
                  </div>
                )}

                {this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST_EXPANDED && (
                  <div class={pfx("content-aggregator-item__list-expanded--links")}>
                    <slot name="links"></slot>
                  </div>
                )}
              </div>

              {this.variant === CONTENT_AGGREGATOR_ITEM_LAYOUT_LIST && (
                <div class={pfx(`content-aggregator-item__list--button ${this.hideTime ? "hide-time" : "show-time"}`)}>
                  <eon-ui-button
                    use-as-link="true"
                    href={this.headlineHref}
                    target={this.linkTarget as ButtonTarget}
                    icon="arrow_right"
                    rank="primary"
                    scheme={
                      this.activeScheme.listButtonScheme as keyof ButtonSchemeConfiguration<ButtonSchemeDefinition>
                    }
                  ></eon-ui-button>
                </div>
              )}
            </div>
          </a>
        ) : (
          <eon-ui-background color={this.activeScheme.backgroundColor} invisible>
            <div class={pfx("content-aggregator-item__tile")}>
              {this.backgroundImage && (
                <div class={pfx("content-aggregator-item__tile--image-layer")}>
                  <slot name="image"></slot>
                </div>
              )}
              <div
                class={pfx({
                  ["content-aggregator-item__tile--content-layer"]: true,
                  ["on-background-image"]: this.backgroundImage
                })}
              >
                <div
                  class={pfx({
                    ["content-aggregator-item__tile--date-category"]: true,
                    ["on-background-image"]: this.backgroundImage
                  })}
                >
                  {this.dateInput && this.date ? (
                    this.handleDateEventComponent()
                  ) : (
                    <div class={pfx("content-aggregator-item__tile--category")}>{this.category}</div>
                  )}
                </div>
                {this.headlineText && (
                  <a
                    ref={(el) => (this.anchorElement = el)}
                    href={this.headlineHref}
                    target={this.linkTarget}
                    class={pfx(`content-aggregator-item__tile--headline-as-link`)}
                    tabIndex={0}
                  >
                    {this.handleHeadlineComponent()}
                  </a>
                )}
                {this.showAbstract && (
                  <div
                    class={pfx({
                      ["content-aggregator-item__tile--content"]: true
                    })}
                  >
                    <slot name="content"></slot>
                  </div>
                )}
                <div class={pfx("content-aggregator-item__tile--links")}>
                  <slot name="links"></slot>
                </div>
              </div>
            </div>
          </eon-ui-background>
        )}
      </Host>
    );
  }
}
